import FastMarquee from "react-fast-marquee";
import { motion } from "framer-motion";

/**
 * Times-Square style LED billboard.
 * Three stacked streams at different speeds + directions,
 * with LED-dot overlay, faint scanlines, and a live indicator.
 */

const LINE_A = ["R.I.V.H™", "FIND YOUR PURPOSE", "CAPSULE 01 — MONOLITH"];
const LINE_B = [
  "REAL",
  "INTELLIGENT",
  "VALUABLE",
  "HUMANS",
];
const LINE_C = [
  "THE X BAG — $350",
  "OPTIC 001 AMETHYST — $175",
  "THE CASE — INCLUDED",
  "DOORS OPEN 02.14.26",
];

function Sep({ symbol = "✦" }) {
  return (
    <span className="mx-6 md:mx-10 text-white/40 font-display text-3xl md:text-5xl">
      {symbol}
    </span>
  );
}

function Ticker({ items, speed, direction = "left", size = "xl" }) {
  const sizeClass =
    size === "xl"
      ? "text-6xl md:text-[7rem]"
      : size === "lg"
        ? "text-4xl md:text-6xl"
        : "text-2xl md:text-4xl";
  return (
    <div className="relative overflow-hidden">
      <FastMarquee
        gradient={false}
        speed={speed}
        direction={direction}
        pauseOnHover={false}
      >
        {Array.from({ length: 6 }).map((_, r) => (
          <span key={r} className="flex items-center">
            {items.map((t, i) => (
              <span key={i} className="flex items-center">
                <span
                  className={`font-display uppercase text-white tracking-[-0.02em] mx-6 md:mx-10 whitespace-nowrap ${sizeClass}`}
                  style={{
                    textShadow:
                      "0 0 18px rgba(255,255,255,0.35), 0 0 42px rgba(255,255,255,0.18)",
                  }}
                >
                  {t}
                </span>
                <Sep symbol={i % 2 === 0 ? "✦" : "✕"} />
              </span>
            ))}
          </span>
        ))}
      </FastMarquee>
    </div>
  );
}

export default function EditorialMarquee() {
  return (
    <section
      data-testid="editorial-marquee"
      className="relative border-y border-white/15 bg-[#050505] overflow-hidden select-none"
    >
      {/* Deep base for LED glow */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,#0d0d0d_0%,#050505_60%,#000_100%)]"
      />

      {/* LED "on air" bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 py-4 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
        <span className="flex items-center gap-3">
          <motion.span
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block w-2 h-2 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.9)]"
          />
          <span data-testid="billboard-live">LIVE / ON AIR</span>
        </span>
        <span className="hidden sm:inline">R.I.V.H™ / BILLBOARD 01</span>
        <span>FIND YOUR PURPOSE</span>
      </div>

      {/* Ticker streams */}
      <div className="relative z-10 py-6 md:py-8">
        <Ticker items={LINE_A} speed={70} direction="left" size="xl" />
        <div className="my-3 md:my-4 border-t border-white/10" />
        <Ticker items={LINE_B} speed={110} direction="right" size="lg" />
        <div className="my-3 md:my-4 border-t border-white/10" />
        <Ticker items={LINE_C} speed={55} direction="left" size="sm" />
      </div>

      {/* LED dot matrix overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 opacity-[0.28] mix-blend-multiply"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, transparent 45%, #000 55%)",
          backgroundSize: "4px 4px",
        }}
      />

      {/* Faint scanlines */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 opacity-[0.18]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 3px)",
        }}
      />

      {/* Vignette edges */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      {/* Bottom LED bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 py-4 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
        <span>02.14.26 / 40°44′N — 73°59′W</span>
        <span className="hidden sm:inline">Broadcast — orbit 001</span>
        <span>R.I.V.H™</span>
      </div>
    </section>
  );
}
